import { captureUtmFromUrl, getUtmEventParams, getUtmPayloadForApi } from './utm';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
let lastPhoneClickAt = 0;

/**
 * Google Analytics 4 (GA4) Analytics Utility
 * 
 * This utility provides functions to track:
 * - Page views
 * - Custom events (form submissions, button clicks, etc.)
 * - User interactions
 * - Page load performance
 */

// Google Analytics Measurement ID - Replace with your actual GA4 Measurement ID
// Get this from: https://analytics.google.com/ -> Admin -> Data Streams -> Web Stream
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

/** Google Ads account ID (conversion linker / Ads tags) — independent of GA4 stream ID */
const GOOGLE_ADS_ACCOUNT_ID = 'AW-11080462654';

/** Google Ads “Click to call” conversion — send_to must match Google Ads conversion action */
const GOOGLE_ADS_CLICK_TO_CALL_SEND_TO = 'AW-11080462654/rd5JCLzJvaEcEL7iyaMp';

/** Canonical tel: URL for SnagEasy main business line */
export const PHONE_TEL_HREF = 'tel:+917304367001';

/**
 * Google Ads click-to-call conversion (fires before dialer opens via event_callback).
 * Compatible name for Google’s documented HTML snippet (window.gtag_report_conversion).
 */
function logPhoneClickToBackend() {
  const now = Date.now();
  if (now - lastPhoneClickAt < 2000) return;
  lastPhoneClickAt = now;

  const payload = {
    utm: getUtmPayloadForApi(),
    pagePath: window.location.pathname,
    pageUrl: window.location.href,
  };

  try {
    fetch(`${API_BASE_URL}/api/phone-clicks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch((error) => {
      console.warn('Failed to log phone click:', error);
    });
  } catch (error) {
    console.warn('Failed to log phone click:', error);
  }
}

export function reportGoogleAdsClickToCallConversion(url) {
  const telHref = typeof url !== 'undefined' && url ? url : PHONE_TEL_HREF;
  const navigate = () => {
    window.location.href = telHref;
  };

  captureUtmFromUrl();
  logPhoneClickToBackend();
  trackPhoneClick('+917304367001');

  if (!window.gtag) {
    navigate();
    return false;
  }

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_CLICK_TO_CALL_SEND_TO,
    value: 1.0,
    currency: 'INR',
    event_callback: navigate,
  });
  return false;
}

/** Use on tel links: onClick={onBusinessPhoneClick} href={PHONE_TEL_HREF} */
export function onBusinessPhoneClick(e) {
  if (e && typeof e.preventDefault === 'function') {
    e.preventDefault();
  }
  reportGoogleAdsClickToCallConversion(PHONE_TEL_HREF);
}

/** Mobile/programmatic dial without an anchor event */
export function dialBusinessPhoneWithAdsConversion() {
  reportGoogleAdsClickToCallConversion(PHONE_TEL_HREF);
}

// Initialize Google Analytics + Google Ads gtag config
export const initGA = () => {
  captureUtmFromUrl();
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag('js', new Date());

  const hasGa = !!(GA_MEASUREMENT_ID && GA_MEASUREMENT_ID.trim() !== '');
  const loaderId = hasGa ? GA_MEASUREMENT_ID : GOOGLE_ADS_ACCOUNT_ID;

  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${loaderId}`;
  document.head.appendChild(script1);

  if (hasGa) {
    gtag('config', GA_MEASUREMENT_ID, {
      page_path: window.location.pathname,
      send_page_view: false,
    });
  }

  gtag('config', GOOGLE_ADS_ACCOUNT_ID);

  if (typeof window !== 'undefined') {
    window.gtag_report_conversion = reportGoogleAdsClickToCallConversion;
  }

  console.log(
    hasGa ? `Google tag: GA4 ${GA_MEASUREMENT_ID}` : 'Google tag: GA4 not set',
    `Google Ads ${GOOGLE_ADS_ACCOUNT_ID}`
  );
};

// Track page view
export const trackPageView = (path, title) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  const pagePath = path || window.location.pathname + window.location.search;
  // Use provided title, or fallback to document.title, or use pathname as fallback
  const pageTitle = title || document.title || pagePath;

  // Update gtag config with page path and title
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href
  });

  // Also send as event for better tracking (this ensures it shows up in Events report)
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href
  });

  console.log('Page view tracked:', { path: pagePath, title: pageTitle });
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  window.gtag('event', eventName, {
    ...getUtmEventParams(),
    ...eventParams,
    event_category: eventParams.category || 'general',
    event_label: eventParams.label || eventName,
    value: eventParams.value || 0
  });

  console.log('Event tracked:', eventName, eventParams);
};

// Track form submissions
export const trackFormSubmission = (formName, formData = {}) => {
  trackEvent('form_submit', {
    category: 'form',
    label: formName,
    form_name: formName,
    ...formData
  });
};

// Track button clicks
export const trackButtonClick = (buttonName, location = '') => {
  trackEvent('button_click', {
    category: 'engagement',
    label: buttonName,
    button_name: buttonName,
    button_location: location || window.location.pathname
  });
};

// Track external link clicks
export const trackExternalLink = (url, linkText = '') => {
  trackEvent('external_link_click', {
    category: 'outbound',
    label: linkText || url,
    outbound_url: url
  });
};

// Track phone number clicks
export const trackPhoneClick = (phoneNumber) => {
  trackEvent('phone_click', {
    category: 'contact',
    label: phoneNumber,
    phone_number: phoneNumber
  });
};

// Track email clicks
export const trackEmailClick = (email) => {
  trackEvent('email_click', {
    category: 'contact',
    label: email,
    email_address: email
  });
};

// Track search queries
export const trackSearch = (searchTerm, resultsCount = 0) => {
  trackEvent('search', {
    category: 'engagement',
    label: searchTerm,
    search_term: searchTerm,
    results_count: resultsCount
  });
};

// Track video plays
export const trackVideoPlay = (videoTitle, videoUrl = '') => {
  trackEvent('video_play', {
    category: 'media',
    label: videoTitle,
    video_title: videoTitle,
    video_url: videoUrl
  });
};

// Track file downloads
export const trackDownload = (fileName, fileType = '') => {
  trackEvent('file_download', {
    category: 'engagement',
    label: fileName,
    file_name: fileName,
    file_type: fileType
  });
};

// Track scroll depth
export const trackScrollDepth = (depth) => {
  trackEvent('scroll_depth', {
    category: 'engagement',
    label: `${depth}%`,
    scroll_depth: depth
  });
};

// Track time on page
export const trackTimeOnPage = (timeInSeconds, pagePath = '') => {
  trackEvent('time_on_page', {
    category: 'engagement',
    label: pagePath || window.location.pathname,
    time_on_page: timeInSeconds,
    page_path: pagePath || window.location.pathname
  });
};

// Track errors
export const trackError = (errorMessage, errorLocation = '') => {
  trackEvent('error', {
    category: 'error',
    label: errorMessage,
    error_message: errorMessage,
    error_location: errorLocation || window.location.pathname
  });
};

// Track conversion events
export const trackConversion = (conversionType, value = 0, currency = 'INR') => {
  trackEvent('conversion', {
    category: 'conversion',
    label: conversionType,
    conversion_type: conversionType,
    value: value,
    currency: currency
  });
};

// Track booking form submission
export const trackBookingSubmission = (formData) => {
  trackFormSubmission('booking_form', {
    apartment_type: formData.apartmentType,
    pincode: formData.pincode,
    inspection_date: formData.inspectionDate ? 'selected' : 'not_selected'
  });
  
  trackConversion('booking_request', 0, 'INR');
};

// Track partner form submission
export const trackPartnerSubmission = () => {
  trackFormSubmission('partner_form', {});
  trackConversion('partner_application', 0, 'INR');
};

// Track callback request
export const trackCallbackRequest = (requestType) => {
  trackFormSubmission('callback_form', {
    request_type: requestType
  });
  trackConversion('callback_request', 0, 'INR');
};

// Track OTP verification
export const trackOtpVerification = (formType, success = true) => {
  trackEvent('otp_verification', {
    category: 'form',
    label: formType,
    form_type: formType,
    success: success
  });
};

// Set user properties
export const setUserProperty = (propertyName, propertyValue) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  window.gtag('set', 'user_properties', {
    [propertyName]: propertyValue
  });
};

// Set user ID (for logged-in users)
export const setUserId = (userId) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId
  });
};

// Clear user ID (for logout)
export const clearUserId = () => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: null
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackButtonClick,
  trackExternalLink,
  trackPhoneClick,
  trackEmailClick,
  trackSearch,
  trackVideoPlay,
  trackDownload,
  trackScrollDepth,
  trackTimeOnPage,
  trackError,
  trackConversion,
  trackBookingSubmission,
  trackPartnerSubmission,
  trackCallbackRequest,
  trackOtpVerification,
  setUserProperty,
  setUserId,
  clearUserId,
  PHONE_TEL_HREF,
  reportGoogleAdsClickToCallConversion,
  onBusinessPhoneClick,
  dialBusinessPhoneWithAdsConversion,
};

